import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash, Buildings, Lock, EnvelopeSimple, ArrowClockwise, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABELS = {
  organization_owner: "Organization Owner", admin: "Admin",
  sales_manager: "Sales Manager", sales_executive: "Sales Executive",
  accountant: "Accountant", dispatch_manager: "Dispatch Manager",
  marketing_manager: "Marketing Manager", customer_support: "Customer Support",
  custom: "Custom",
};
const ASSIGNABLE = ["admin","sales_manager","sales_executive","accountant","dispatch_manager","marketing_manager","customer_support","custom"];
const BLANK = { name: "", email: "", role: "sales_executive", expires_in_days: 7, permissions: null };

export default function Employees() {
  const { user } = useAuth();
  const isOwner = user?.role === "organization_owner";
  const [users, setUsers] = useState([]);
  const [org, setOrg] = useState(null);
  const [invites, setInvites] = useState([]);
  const [meta, setMeta] = useState({ permissions: [], defaults: {} });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(BLANK);
  const [editingCustom, setEditingCustom] = useState(false);
  const [permsList, setPermsList] = useState([]);
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessUser, setAccessUser] = useState(null);
  const [accessRole, setAccessRole] = useState("sales_executive");
  const [accessPerms, setAccessPerms] = useState([]);
  const [savingAccess, setSavingAccess] = useState(false);

  const load = async () => {
    const [u, o, r] = await Promise.all([api.get("/users"), api.get("/organization"), api.get("/roles")]);
    setUsers(u.data); setOrg(o.data);
    setMeta({ permissions: r.data.permissions, defaults: r.data.defaults });
    if (isOwner) {
      try { const i = await api.get("/invitations"); setInvites(i.data); }
      catch (err) {
        if (process.env.NODE_ENV !== "production") console.warn("loadInvites failed", err);
      }
    }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const openInvite = () => {
    setF(BLANK); setEditingCustom(false);
    setPermsList(meta.defaults?.sales_executive || []);
    setOpen(true);
  };

  const onRoleChange = (role) => {
    setF({ ...f, role });
    setEditingCustom(role === "custom");
    if (role !== "custom") setPermsList(meta.defaults?.[role] || []);
  };

  const togglePerm = (p) => setPermsList((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);

  const invite = async () => {
    if (!f.name || !f.email) return toast.error("Name & email required");
    try {
      const body = { name: f.name, email: f.email, role: f.role, expires_in_days: +f.expires_in_days || 7 };
      if (editingCustom) body.permissions = permsList;
      await api.post("/invitations", body);
      toast.success("Invitation email sent");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cancelInvite = async (i) => {
    if (!window.confirm(`Cancel invitation for ${i.email}?`)) return;
    await api.delete(`/invitations/${i.id}`);
    load();
  };

  const toggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { active: !u.active });
    load();
  };

  const setManager = async (u, managerId) => {
    try {
      await api.put(`/users/${u.id}`, { manager_id: managerId || "" });
      toast.success(managerId ? "Manager assigned" : "Manager removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setManagerScope = async (u, scopeVal) => {
    try {
      await api.put(`/users/${u.id}`, { manager_scope: scopeVal });
      toast.success(`Scope: ${scopeVal}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name} from your workspace?`)) return;
    try {
      await api.delete(`/users/${u.id}`); toast.success("Removed"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openAccess = (u) => {
    setAccessUser(u);
    setAccessRole(u.role);
    setAccessPerms(u.permissions || []);
    setAccessOpen(true);
  };

  const toggleAccessPerm = (p) =>
    setAccessPerms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);

  const applyRoleDefaults = () => {
    if (accessRole === "custom") return;
    setAccessPerms(meta.defaults?.[accessRole] || []);
    toast.success("Loaded default permissions for this role");
  };

  const saveAccess = async () => {
    if (!accessUser) return;
    setSavingAccess(true);
    try {
      const body = { role: accessRole, permissions: accessPerms };
      await api.put(`/users/${accessUser.id}`, body);
      toast.success(`Access updated for ${accessUser.name}`);
      setAccessOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update access"); }
    finally { setSavingAccess(false); }
  };

  const resetOnePermissions = async (u) => {
    if (!window.confirm(`Reset ${u.name}'s permissions to defaults for their role?`)) return;
    try {
      await api.post(`/users/${u.id}/reset-permissions`);
      toast.success("Permissions reset to role defaults");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resetAllPermissions = async () => {
    if (!window.confirm("Refresh every non-owner member's permissions to the current defaults for their role? Members with genuine custom perm sets will be left alone.")) return;
    try {
      const r = await api.post("/users/reset-all-permissions");
      toast.success(`Refreshed ${r.data.refreshed} member(s), skipped ${r.data.skipped}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const pending = invites.filter((i) => i.status === "pending");
  const managers = users.filter((u) => ["sales_manager", "admin", "organization_owner"].includes(u.role));
  const userById = Object.fromEntries(users.map((u) => [u.id, u]));

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline text-muted-foreground mb-2">Access</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Team & Roles</h1>
          {org && (
            <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
              <Buildings size={14} weight="duotone" />
              <span>Workspace: <span className="text-foreground font-medium">{org.name}</span></span>
              <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-secondary">{org.id?.slice(0,8)}</span>
            </div>
          )}
        </div>
        {isOwner && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" className="rounded-full" onClick={resetAllPermissions} data-testid="reset-all-perms-button">
              <ArrowClockwise size={16} className="mr-2" />Refresh all permissions
            </Button>
            <Button className="rounded-full" onClick={openInvite} data-testid="invite-member-button">
              <Plus size={16} className="mr-2" />Invite Team Member
            </Button>
          </div>
        )}
      </div>

      {!isOwner && (
        <Card className="p-5 border-primary/30 bg-primary/5">
          <div className="flex items-start gap-3">
            <Lock size={18} weight="duotone" className="text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-sm">Only the organization owner can invite or manage team members.</div>
              <div className="text-xs text-muted-foreground mt-1">You can still see everyone in your workspace below.</div>
            </div>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border/40 font-heading font-bold text-sm">Active Members · {users.length}</div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
            <tr><th className="text-left p-4">Name</th><th className="text-left p-4">Email</th><th className="text-left p-4">Role</th><th className="text-left p-4">Reports to / Scope</th><th className="text-left p-4">Status</th><th></th></tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-t border-border/40" data-testid={`user-${u.id}`}>
                <td className="p-4 font-medium">{u.name}</td>
                <td className="p-4 text-muted-foreground">{u.email}</td>
                <td className="p-4"><span className="text-xs px-2 py-1 rounded-full bg-secondary capitalize">{ROLE_LABELS[u.role] || u.role}</span></td>
                <td className="p-4 text-xs">
                  {isOwner && u.role === "sales_executive" && u.id !== user.id ? (
                    <Select value={u.manager_id || "__none__"} onValueChange={(v) => setManager(u, v === "__none__" ? "" : v)}>
                      <SelectTrigger className="h-8 w-44" data-testid={`manager-select-${u.id}`}><SelectValue placeholder="No manager" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— No manager —</SelectItem>
                        {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : isOwner && u.role === "sales_manager" && u.id !== user.id ? (
                    <Select value={u.manager_scope || "team"} onValueChange={(v) => setManagerScope(u, v)}>
                      <SelectTrigger className="h-8 w-44" data-testid={`scope-select-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="own">Own only</SelectItem>
                        <SelectItem value="team">Own + Team</SelectItem>
                        <SelectItem value="all">All customers</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : u.role === "sales_executive" && u.manager_id ? (
                    <span className="flex items-center gap-1 text-muted-foreground"><UsersThree size={12} weight="duotone" />{userById[u.manager_id]?.name || "—"}</span>
                  ) : u.role === "sales_manager" ? (
                    <span className="text-muted-foreground">Scope: {u.manager_scope || "team"}</span>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${u.active ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-muted text-muted-foreground"}`}>{u.active ? "Active" : "Inactive"}</span></td>
                <td className="p-4 text-right whitespace-nowrap">
                  {isOwner && u.id !== user.id && (
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openAccess(u)} className="text-xs px-2 py-1 rounded border border-border hover:bg-secondary" data-testid={`edit-access-${u.id}`}>Edit Access</button>
                      <button onClick={() => toggleActive(u)} className="text-xs px-2 py-1 rounded border border-border hover:bg-secondary">{u.active ? "Deactivate" : "Activate"}</button>
                      <button onClick={() => remove(u)} className="p-1.5 text-destructive hover:bg-destructive/10 rounded"><Trash size={14} /></button>
                    </div>
                  )}
                  {u.id === user.id && <span className="text-xs text-muted-foreground">You</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {isOwner && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-border/40 flex items-center gap-2">
            <EnvelopeSimple size={16} weight="duotone" className="text-primary" />
            <span className="font-heading font-bold text-sm">Pending Invitations · {pending.length}</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
              <tr><th className="text-left p-4">Email</th><th className="text-left p-4">Name</th><th className="text-left p-4">Role</th><th className="text-left p-4">Expires</th><th className="text-left p-4">Status</th><th></th></tr>
            </thead>
            <tbody>
              {invites.map(i => (
                <tr key={i.id} className="border-t border-border/40" data-testid={`inv-${i.id}`}>
                  <td className="p-4">{i.email}</td>
                  <td className="p-4 text-muted-foreground">{i.name}</td>
                  <td className="p-4"><span className="text-xs px-2 py-1 rounded-full bg-secondary">{ROLE_LABELS[i.role] || i.role}</span></td>
                  <td className="p-4 text-xs text-muted-foreground">{new Date(i.expires_at).toLocaleDateString("en-IN")}</td>
                  <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${i.status==='pending'?'bg-amber-500/10 text-amber-700 dark:text-amber-300':i.status==='accepted'?'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300':'bg-muted text-muted-foreground'}`}>{i.status}</span></td>
                  <td className="p-4 text-right">
                    {i.status === "pending" && <button onClick={() => cancelInvite(i)} className="text-xs px-2 py-1 rounded border border-border hover:bg-secondary" data-testid={`cancel-inv-${i.id}`}>Cancel</button>}
                  </td>
                </tr>
              ))}
              {!invites.length && <tr><td colSpan={6} className="p-6 text-center text-xs text-muted-foreground">No invitations yet.</td></tr>}
            </tbody>
          </table>
        </Card>
      )}

      {isOwner && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Invite team member</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label className="text-xs">FULL NAME</Label><Input value={f.name} onChange={(e) => setF({...f,name:e.target.value})} data-testid="inv-name" /></div>
              <div><Label className="text-xs">EMAIL</Label><Input type="email" value={f.email} onChange={(e) => setF({...f,email:e.target.value})} data-testid="inv-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">ROLE</Label>
                  <Select value={f.role} onValueChange={onRoleChange}>
                    <SelectTrigger data-testid="inv-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ASSIGNABLE.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">EXPIRES IN (DAYS)</Label><Input type="number" min={1} max={30} value={f.expires_in_days} onChange={(e) => setF({...f,expires_in_days:e.target.value})} /></div>
              </div>
              {editingCustom && (
                <div>
                  <Label className="text-xs mb-2 block">PERMISSIONS</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {meta.permissions.map(p => (
                      <label key={p} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={permsList.includes(p)} onCheckedChange={() => togglePerm(p)} data-testid={`perm-${p}`} />
                        <span className="capitalize">{p.replace("_"," ")}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {!editingCustom && (
                <div className="p-3 rounded bg-secondary/40 text-xs">
                  <div className="font-medium mb-1">Default access for this role:</div>
                  <div className="flex flex-wrap gap-1">
                    {(meta.defaults?.[f.role] || []).map(p => (
                      <span key={p} className="px-2 py-0.5 rounded bg-background text-[11px]">{p.replace("_"," ")}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-xs text-muted-foreground">The invitee will receive an email with a link to set their password and join your workspace.</div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={invite} data-testid="send-invite-button">Send invitation</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {isOwner && (
        <Dialog open={accessOpen} onOpenChange={setAccessOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-access-dialog">
            <DialogHeader>
              <DialogTitle>Edit access · {accessUser?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs">ROLE</Label>
                <Select value={accessRole} onValueChange={(v) => { setAccessRole(v); if (v !== "custom") setAccessPerms(meta.defaults?.[v] || []); }}>
                  <SelectTrigger data-testid="access-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ASSIGNABLE.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs">PERMISSIONS</Label>
                  {accessRole !== "custom" && (
                    <button onClick={applyRoleDefaults} className="text-[11px] text-primary hover:underline" data-testid="apply-role-defaults">
                      Load defaults for {ROLE_LABELS[accessRole]}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 border border-border/60 rounded-lg p-3 bg-secondary/20">
                  {meta.permissions.map(p => (
                    <label key={p} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={accessPerms.includes(p)} onCheckedChange={() => toggleAccessPerm(p)} data-testid={`access-perm-${p}`} />
                      <span className="capitalize">{p.replace("_"," ")}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Tick a module (e.g. <span className="font-mono">inventory</span>) to grant every action, or use fine-grained keys like <span className="font-mono">inventory.view</span> for read-only access.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => resetOnePermissions(accessUser)} data-testid="reset-one-perms">Reset to role defaults</Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={() => setAccessOpen(false)}>Cancel</Button>
              <Button onClick={saveAccess} disabled={savingAccess} data-testid="save-access">
                {savingAccess ? "Saving…" : "Save access"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
